if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tabBtns = [...this.querySelectorAll('.collection-list-with-image__tab')];
      this.panels = [...this.querySelectorAll('.collection-list-with-image__panel')];
      this.viewAllLinks = [...this.querySelectorAll('.collection-list-with-image__view-all-link')];

      this.tabBtns.forEach((btn, i) => {
        btn.addEventListener('click', () => this.switchTab(i));
      });

      this._syncViewAllLink(0);
    }

    switchTab(index) {
      this.tabBtns.forEach((btn, i) => {
        btn.classList.toggle('collection-list-with-image__tab--active', i === index);
      });

      this.panels.forEach((panel, i) => {
        panel.classList.toggle('collection-list-with-image__panel--active', i === index);
      });

      this._syncViewAllLink(index);

      const slider = this.panels[index]?.querySelector('collection-list-slider');
      if (slider) {
        requestAnimationFrame(() => {
          slider._applyLayout();
          slider.setPosition(false);
          slider.updateButtons();
        });
      }
    }

    _syncViewAllLink(index) {
      const panel = this.panels[index];
      const url = panel?.dataset.url;
      const label = panel?.dataset.label || '';
      this.viewAllLinks.forEach(link => {
        const base = link.dataset.baseLabel || link.textContent.trim();
        link.textContent = label ? `${base} ${label}` : base;
        if (url) {
          link.href = url;
          link.style.visibility = '';
        } else {
          link.style.visibility = 'hidden';
        }
      });
    }
  }

  customElements.define('collection-tabs', CollectionTabs);
}

if (!customElements.get('collection-list-slider')) {
  class CollectionListSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.collection-list-with-image__slider-viewport');
      this.track = this.querySelector('.collection-list-with-image__slider-track');
      this.prevBtn = this.querySelector('.collection-list-with-image__slider-btn--prev');
      this.nextBtn = this.querySelector('.collection-list-with-image__slider-btn--next');

      this.currentIndex = 0;
      this._slideWidth = 0;
      this._gap = 0;
      this._totalSlides = this.track.children.length;

      this.prevBtn.addEventListener('click', () => this.prev());
      this.nextBtn.addEventListener('click', () => this.next());

      requestAnimationFrame(() => {
        if (window.innerWidth < 750) this._applyLayout();
      });

      this.resizeObserver = new ResizeObserver(() => {
        if (window.innerWidth < 750) return;
        this._applyLayout();
        this.setPosition(false);
        this.updateButtons();
      });

      this.resizeObserver.observe(this.viewport);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    get slidesPerView() {
      const desktop = parseInt(this.dataset.slidesPerView) || 5;
      return window.innerWidth < 990 ? Math.min(desktop, 4) : desktop;
    }

    get _isFit() {
      return this._totalSlides <= this.slidesPerView;
    }

    _applyLayout() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this._gap = gap;

      const vw = this.viewport.offsetWidth;
      if (!vw) return;

      if (window.innerWidth < 750) {
        // 2.5 items visible: 2 full + half of third, with 2 gaps between them
        const mobileWidth = (vw - 2 * gap) / 2.5;
        [...this.track.children].forEach(item => {
          item.style.width = `${Math.floor(mobileWidth)}px`;
        });
        return;
      }

      const count = this._isFit ? this._totalSlides : this.slidesPerView;
      this._slideWidth = (vw - (count - 1) * gap) / count;

      [...this.track.children].forEach(item => {
        item.style.width = `${this._slideWidth}px`;
      });

      this.prevBtn.style.display = this._isFit ? 'none' : '';
      this.nextBtn.style.display = this._isFit ? 'none' : '';
    }

    getTranslateX(index) {
      return -(index * (this._slideWidth + this._gap));
    }

    setPosition(animate) {
      if (window.innerWidth < 750) return;
      if (this._isFit) {
        this.track.style.transition = 'none';
        this.track.style.transform = 'translateX(0)';
        return;
      }
      const x = this.getTranslateX(this.currentIndex);
      this.track.style.transition = animate ? 'transform 0.4s ease' : 'none';
      this.track.style.transform = `translateX(${x}px)`;
    }

    updateButtons() {
      if (window.innerWidth < 750 || this._isFit) return;
      const maxIndex = Math.max(0, this._totalSlides - this.slidesPerView);
      this.prevBtn.disabled = this.currentIndex <= 0;
      this.nextBtn.disabled = this.currentIndex >= maxIndex;
    }

    next() {
      const maxIndex = Math.max(0, this._totalSlides - this.slidesPerView);
      if (this.currentIndex >= maxIndex) return;
      this.currentIndex++;
      this.setPosition(true);
      this.updateButtons();
    }

    prev() {
      if (this.currentIndex <= 0) return;
      this.currentIndex--;
      this.setPosition(true);
      this.updateButtons();
    }
  }

  customElements.define('collection-list-slider', CollectionListSlider);
}
